import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getApprovalStatus } from '@/lib/approvalStatus';
import {
  getActiveRegionConfig,
  getPortalHeaders,
  getPrimaryHostForRegion,
  getRegionCodeFromHostname,
  getRegionCodeFromMetadata,
} from '@/lib/regionConfig';
import supabase from '@/lib/supabaseClient';
import { captchaOptions } from '@/lib/turnstile';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState(false);

  useEffect(() => {
    // Load active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Recovery session — flag it but do NOT grant full app access
        setIsRecoverySession(true);
        setUser(null);
        setSession(null);
        return;
      }

      setIsRecoverySession(false);
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;

    const userRegion = getRegionCodeFromMetadata(user.user_metadata);
    if (!userRegion) return;

    const currentHost = window.location.hostname.toLowerCase();
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') return;

    // Only redirect when this host belongs to a *different* region. Preview/dev
    // hosts for the user's own region (and unmapped hosts such as *.vercel.app)
    // must stay put -- the primary host is production, on a different Supabase.
    const hostRegion = getRegionCodeFromHostname(currentHost);
    if (!hostRegion || hostRegion === userRegion) return;
    if (userRegion === 'UKSEF' && hostRegion === 'UK') return;

    const expectedHost = getPrimaryHostForRegion(userRegion);
    if (currentHost === expectedHost) return;

    window.location.replace(
      `https://${expectedHost}${window.location.pathname}${window.location.search}${window.location.hash}`
    );
  }, [user]);

  const signIn = useCallback(async (email, password, captchaToken) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaOptions(captchaToken),
    });
    if (error) return { data, error };

    // Block login if the account hasn't been admin-approved yet
    const userId = data.user?.id;
    if (userId) {
      const accessToken = data.session?.access_token;
      const approval = await getApprovalStatus({ userId, accessToken });
      if (approval.adminApproved === false) {
        if (accessToken) {
          try {
            const regionConfig = getActiveRegionConfig();
            await fetch(`${API_URL}/api/auth/post-verify`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${accessToken}`, ...getPortalHeaders(regionConfig) },
            });
          } catch (notifyErr) {
            console.error('Pending approval re-notify failed:', notifyErr);
          }
        }
        await supabase.auth.signOut();
        return {
          data: null,
          error: { message: 'PENDING_APPROVAL' },
        };
      }

      if (approval.error) {
        console.warn('signIn: approval check failed, allowing login', approval.error);
      }
    }

    return { data, error };
  }, []);

  const signUp = useCallback(async (email, password, fullName, metadata = {}, captchaToken) => {
    const redirectTo =
      typeof window !== 'undefined'
        ? `${window.location.origin}/auth/callback`
        : process.env.REACT_APP_EMAIL_REDIRECT_URL;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, ...metadata },
        emailRedirectTo: redirectTo,
        ...captchaOptions(captchaToken),
      },
    });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({ user, session, loading, signIn, signUp, signOut, isRecoverySession }),
    [user, session, loading, signIn, signUp, signOut, isRecoverySession],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

export default AuthContext;
